import {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Icon,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { toast } from "sonner";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { useRenameReport } from "../../hooks/useRenameReport";
import type { PreflightReportSummary } from "../../types/preflight.types";

export interface ReportNameCellProps {
  report: Pick<PreflightReportSummary, "id" | "name">;
  /** Hide rename while a report is still processing. */
  canRename?: boolean;
  /** Parent row is hovered — reveals the pencil trigger. */
  isRowHovered?: boolean;
  /** Notifies the parent when inline edit opens or closes (e.g. to widen the cell). */
  onEditingChange?: (isEditing: boolean) => void;
}

const RENAME_INPUT_CLASS =
  "border-primary/40 focus-visible:border-primary focus-visible:ring-primary/20 h-8 min-w-0 flex-1 rounded-sm bg-background px-2 text-sm shadow-none focus-visible:ring-1";

/**
 * Report title with a hover pencil for inline rename (past-reports R6).
 * Only the pencil starts the edit — a click on the title falls through to the
 * row and opens the report. Enter/blur saves; Escape cancels. Text is selected
 * when edit opens.
 */
export function ReportNameCell({
  report,
  canRename = true,
  isRowHovered = false,
  onEditingChange,
}: ReportNameCellProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const renameMutation = useRenameReport(report);
  const inputRef = useRef<HTMLInputElement>(null);
  const ignoreBlurCommitRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLocalHovered, setIsLocalHovered] = useState(false);
  const [draft, setDraft] = useState(report.name);

  const canPersistRename = permissions.PREFLIGHT_RENAME_REPORT.isOn;
  const showRenameTrigger = canRename && (isRowHovered || isLocalHovered);

  useLayoutEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  useLayoutEffect(() => {
    if (!isEditing) return;

    ignoreBlurCommitRef.current = true;
    const input = inputRef.current;
    if (input) {
      input.focus();
      input.select();
    }

    const frame = window.requestAnimationFrame(() => {
      ignoreBlurCommitRef.current = false;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isEditing]);

  function cancelEdit() {
    setDraft(report.name);
    setIsEditing(false);
  }

  function commitEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === report.name) {
      cancelEdit();
      return;
    }

    renameMutation.mutate(trimmed, {
      onSettled: () => setIsEditing(false),
    });
  }

  function handleBlur() {
    if (ignoreBlurCommitRef.current) return;
    commitEdit();
  }

  function startEditing(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    if (!canRename) return;

    if (!canPersistRename) {
      toast.error(t("preflight.toast.renameForbidden"));
      return;
    }

    setDraft(report.name);
    setIsEditing(true);
  }

  if (isEditing) {
    return (
      <div className="flex w-full min-w-0 items-center gap-1.5">
        <Input
          ref={inputRef}
          value={draft}
          disabled={renameMutation.isPending}
          onChange={(event) => setDraft(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.target.select()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitEdit();
            } else if (event.key === "Escape") {
              event.preventDefault();
              cancelEdit();
            }
          }}
          onBlur={handleBlur}
          className={RENAME_INPUT_CLASS}
          aria-label={t("preflight.rename.label")}
        />
        <span className="size-5 shrink-0" aria-hidden />
      </div>
    );
  }

  const nameLabel = (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className="text-foreground min-w-0 flex-1 truncate text-sm">
          {report.name}
        </p>
      </TooltipTrigger>
      <TooltipContent side="top">{report.name}</TooltipContent>
    </Tooltip>
  );

  if (!canRename) {
    return nameLabel;
  }

  return (
    <div
      className="flex min-w-0 w-full items-center gap-1.5"
      onMouseEnter={() => setIsLocalHovered(true)}
      onMouseLeave={() => setIsLocalHovered(false)}
    >
      {nameLabel}
      <button
        type="button"
        data-report-rename-trigger
        title={t("preflight.rename.title")}
        aria-label={t("preflight.rename.trigger")}
        className={cn(
          "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded border transition-opacity group-hover/row:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:min-h-5 [@media(hover:hover)_and_(pointer:fine)]:min-w-5",
          showRenameTrigger ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onMouseDown={(event) => event.preventDefault()}
        onClick={startEditing}
      >
        <Icon name="pencil" size="xs" />
      </button>
    </div>
  );
}
